import { i18n } from "@tellescope/i18n";

import type { TokenGroupId } from "./types";

/**
 * Static token catalogs that feed the registry. Each field's label is a getter wrapping
 * `i18n.t(...)` with a literal string (namespace `templates`) so it stays statically extractable
 * and resolves against the current language at read time. These describe the *built-in* fields a
 * namespace exposes; arbitrary custom fields are still recognized and preserved on load even
 * though they are not enumerated here.
 *
 * SECURITY: `hashedPassword` is intentionally absent from every namespace and must never be added —
 * the resolver blocks it server-side and the picker must not offer it.
 */

/** A built-in field within a namespace: the field path and its translated label. */
export interface NamespaceField {
  /** Field path appended after the namespace dot, e.g. `"fname"` or `"insurance.payerName"`. */
  field: string;
  /** Translated friendly label. */
  getLabel: () => string;
}

/** Namespaces whose field names are HTML-unescaped (`&amp;` → `&`) on recognition. */
export const AMPERSAND_NORMALIZED_NAMESPACES = ["enduser", "relatedcontact", "organization", "brand"] as const;

/** Human-readable group headings for the palette, keyed by stable group id. */
export const GROUP_LABELS: Record<TokenGroupId, () => string> = {
  patient: () => i18n.t("Patient", { ns: "templates" }),
  relatedContact: () => i18n.t("Related Contact", { ns: "templates" }),
  sender: () => i18n.t("Sender", { ns: "templates" }),
  organization: () => i18n.t("Organization", { ns: "templates" }),
  brand: () => i18n.t("Brand", { ns: "templates" }),
  calendarEvent: () => i18n.t("Appointment", { ns: "templates" }),
  links: () => i18n.t("Links", { ns: "templates" }),
  signature: () => i18n.t("Signature", { ns: "templates" }),
  snippet: () => i18n.t("Snippets", { ns: "templates" }),
  placeholder: () => i18n.t("Placeholders", { ns: "templates" }),
  other: () => i18n.t("Other", { ns: "templates" }),
};

/** Built-in + computed `enduser` fields (also reused for `relatedcontact`). */
export const ENDUSER_FIELDS: NamespaceField[] = [
  { field: "id", getLabel: () => i18n.t("Enduser ID", { ns: "templates" }) },
  { field: "fname", getLabel: () => i18n.t("First Name", { ns: "templates" }) },
  { field: "lname", getLabel: () => i18n.t("Last Name", { ns: "templates" }) },
  { field: "email", getLabel: () => i18n.t("Email", { ns: "templates" }) },
  { field: "phone", getLabel: () => i18n.t("Phone", { ns: "templates" }) },
  { field: "dateOfBirth", getLabel: () => i18n.t("Date of Birth", { ns: "templates" }) },
  { field: "Age", getLabel: () => i18n.t("Age", { ns: "templates" }) },
  { field: "BMI", getLabel: () => i18n.t("BMI", { ns: "templates" }) },
  { field: "height.value", getLabel: () => i18n.t("Height Value", { ns: "templates" }) },
  { field: "height.unit", getLabel: () => i18n.t("Height Unit", { ns: "templates" }) },
  { field: "weight.value", getLabel: () => i18n.t("Weight Value", { ns: "templates" }) },
  { field: "weight.unit", getLabel: () => i18n.t("Weight Unit", { ns: "templates" }) },
  { field: "insurance.payerName", getLabel: () => i18n.t("Insurance Payer Name", { ns: "templates" }) },
  {
    field: "insuranceSecondary.payerName",
    getLabel: () => i18n.t("Secondary Insurance Payer Name", { ns: "templates" }),
  },
  { field: "Healthie ID", getLabel: () => i18n.t("Healthie ID", { ns: "templates" }) },
  { field: "profileURL", getLabel: () => i18n.t("Profile URL", { ns: "templates" }) },
  { field: "Preferred Name", getLabel: () => i18n.t("Preferred Name", { ns: "templates" }) },
];

/** Built-in `sender` (sending user/provider) fields. */
export const SENDER_FIELDS: NamespaceField[] = [
  { field: "id", getLabel: () => i18n.t("Sender ID", { ns: "templates" }) },
  { field: "fname", getLabel: () => i18n.t("Sender First Name", { ns: "templates" }) },
  { field: "lname", getLabel: () => i18n.t("Sender Last Name", { ns: "templates" }) },
  { field: "email", getLabel: () => i18n.t("Sender Email", { ns: "templates" }) },
  { field: "url", getLabel: () => i18n.t("Sender URL", { ns: "templates" }) },
  { field: "bio", getLabel: () => i18n.t("Sender Bio", { ns: "templates" }) },
];

/** Brand-aware (portal-branding) fields. */
export const BRAND_FIELDS: NamespaceField[] = [
  { field: "name", getLabel: () => i18n.t("Brand Name", { ns: "templates" }) },
  { field: "primaryColor", getLabel: () => i18n.t("Brand Primary Color", { ns: "templates" }) },
  { field: "secondaryColor", getLabel: () => i18n.t("Brand Secondary Color", { ns: "templates" }) },
  { field: "logo", getLabel: () => i18n.t("Brand Logo URL", { ns: "templates" }) },
];

/** The four organization fields the picker exposes. */
export const ORGANIZATION_FIELDS: NamespaceField[] = [
  { field: "name", getLabel: () => i18n.t("Organization Name", { ns: "templates" }) },
  { field: "themeColor", getLabel: () => i18n.t("Primary Color", { ns: "templates" }) },
  { field: "themeColorSecondary", getLabel: () => i18n.t("Secondary Color", { ns: "templates" }) },
  { field: "logo", getLabel: () => i18n.t("Logo URL", { ns: "templates" }) },
];

/** Non-link `calendar_event` fields (portal/ics links are parameterized link tokens). */
export const CALENDAR_EVENT_FIELDS: NamespaceField[] = [
  { field: "title", getLabel: () => i18n.t("Event Title", { ns: "templates" }) },
  { field: "start_date", getLabel: () => i18n.t("Event Start Date", { ns: "templates" }) },
  { field: "start_time", getLabel: () => i18n.t("Event Start Time", { ns: "templates" }) },
  { field: "start_date_time", getLabel: () => i18n.t("Event Start Date and Time", { ns: "templates" }) },
  { field: "Healthie ID", getLabel: () => i18n.t("Event Healthie ID", { ns: "templates" }) },
  { field: "location", getLabel: () => i18n.t("Event Location", { ns: "templates" }) },
  { field: "location.address", getLabel: () => i18n.t("Event Location Address", { ns: "templates" }) },
  { field: "location.instructions", getLabel: () => i18n.t("Event Location Instructions", { ns: "templates" }) },
  { field: "instructions", getLabel: () => i18n.t("Event Instructions", { ns: "templates" }) },
  { field: "videoURL", getLabel: () => i18n.t("Event Video URL", { ns: "templates" }) },
  { field: "externalVideoURL", getLabel: () => i18n.t("Event External Video URL", { ns: "templates" }) },
  { field: "healthieZoomJoinURL", getLabel: () => i18n.t("Event Zoom Join URL", { ns: "templates" }) },
  { field: "healthieZoomStartURL", getLabel: () => i18n.t("Event Zoom Host URL", { ns: "templates" }) },
  { field: "add_to_gcal_link", getLabel: () => i18n.t("Add to Google Calendar Link", { ns: "templates" }) },
  { field: "host", getLabel: () => i18n.t("Event Host Name", { ns: "templates" }) },
];

/** Signature tokens (whole-token form, no namespace dot). */
export const SIGNATURE_TOKENS: NamespaceField[] = [
  { field: "SIGNATURE", getLabel: () => i18n.t("Signature (all channels)", { ns: "templates" }) },
  { field: "SIGNATURE.EMAIL", getLabel: () => i18n.t("Signature (email only)", { ns: "templates" }) },
];

/** Standalone whole-token values. */
export const STANDALONE_TOKENS: NamespaceField[] = [
  { field: "CURRENT_DATE", getLabel: () => i18n.t("Current Date", { ns: "templates" }) },
  { field: "Junction Portal URL", getLabel: () => i18n.t("Junction Portal URL", { ns: "templates" }) },
  { field: "Vital Portal URL", getLabel: () => i18n.t("Vital Portal URL", { ns: "templates" }) },
];

/** Legacy single-curly name tokens, kept for backward compatibility with old templates. */
export const LEGACY_NAME_TOKENS: { token: string; getLabel: () => string }[] = [
  { token: "{name}", getLabel: () => i18n.t("Patient First Name (legacy)", { ns: "templates" }) },
  { token: "{sender}", getLabel: () => i18n.t("Sender Name (legacy)", { ns: "templates" }) },
];

/** Built-in portal destinations offered by the portal-link collector (free text also allowed). */
export const PORTAL_DESTINATIONS: NamespaceField[] = [
  { field: "documents", getLabel: () => i18n.t("Documents", { ns: "templates" }) },
  { field: "appointments", getLabel: () => i18n.t("Appointments", { ns: "templates" }) },
  { field: "messages", getLabel: () => i18n.t("Messages", { ns: "templates" }) },
  { field: "care-plan", getLabel: () => i18n.t("Care Plan", { ns: "templates" }) },
  { field: "_form_response", getLabel: () => i18n.t("Current Form Response", { ns: "templates" }) },
  { field: "_form_group", getLabel: () => i18n.t("Current Form Group", { ns: "templates" }) },
];
